import { API_CONFIG } from '@/lib/api/config';

interface Breadcrumb {
  level: 'Log' | 'Warn' | 'Error';
  message: string;
  timestamp: string;
}

interface BrowserErrorReport {
  message: string;
  stack?: string;
  type: 'Error' | 'Warn' | 'Unhandled';
  pageUrl: string;
  route: string;
  userId: string | null;
  userEmail: string | null;
  workspaceId: string | null;
  companyId: string | null;
  userAgent: string;
  screenSize: string;
  breadcrumbs: Breadcrumb[];
  occurrenceCount: number;
  fingerprint: string;
}

const MAX_BREADCRUMBS = 20;
const DEDUP_TTL_MS = 60_000;
const MAX_BREADCRUMB_MSG = 200;
// Must stay <= the backend ReportClientErrorDto @MaxLength caps, otherwise
// the report 400s and is silently dropped by sendReport's .catch().
const MAX_MESSAGE = 8000;
const MAX_STACK = 12000;

function clamp(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

// A stack frame that points at code the browser itself injected — a password
// manager, an ad blocker, a screen recorder. We do not ship it, cannot read it
// and cannot fix it.
const EXTENSION_FRAME =
  /(?:chrome|moz|ms-browser|safari-web|safari)-extension:\/\//i;
// Any frame that names a source at all, whatever the scheme.
const FRAME_WITH_URL = /\w+:\/\//;
// `window.onerror` reports a cross-origin script's failure as exactly this,
// with no stack and no file — there is nothing in it to act on.
const OPAQUE_CROSS_ORIGIN = /^script error\.?$/i;

/**
 * True when the error did not come from our code.
 *
 * Deliberately CONSERVATIVE — an error is disowned only when it HAS frames and
 * every frame naming a source is an extension frame. An extension that merely
 * appears in the stack of one of OUR failures (a wrapped `fetch`, a patched
 * history method) still reports, because an app frame is present alongside it.
 *
 * Without this, a single staff member's extension produced ~1,000 journal rows
 * a day on production across 20+ routes — the loudest browser signal we had,
 * and none of it actionable. See lib/__tests__/browser-error-reporter.spec.ts.
 */
function isForeignError(message: string, stack: string | undefined): boolean {
  const framesWithUrl = stack
    ? stack.split('\n').slice(1).filter((line) => FRAME_WITH_URL.test(line))
    : [];

  if (framesWithUrl.length === 0) return OPAQUE_CROSS_ORIGIN.test(message.trim());

  return framesWithUrl.every((line) => EXTENSION_FRAME.test(line));
}

// Ring buffer for breadcrumbs
const breadcrumbs: Breadcrumb[] = [];

// Dedup map: fingerprint → { count, timer }
const dedupMap = new Map<string, { count: number; timer: ReturnType<typeof setTimeout>; report: BrowserErrorReport }>();

function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

function getFingerprint(message: string, stack?: string): string {
  const firstLine = stack?.split('\n')[1]?.trim() ?? '';
  return simpleHash(message + firstLine);
}

function addBreadcrumb(level: Breadcrumb['level'], args: unknown[]): void {
  const message = args
    .map((a) => (typeof a === 'string' ? a : JSON.stringify(a)))
    .join(' ')
    .slice(0, MAX_BREADCRUMB_MSG);
  breadcrumbs.push({ level, message, timestamp: new Date().toISOString() });
  if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
}

function getUserContext(): Pick<BrowserErrorReport, 'userId' | 'userEmail' | 'workspaceId' | 'companyId'> {
  try {
    const authRaw = localStorage.getItem('hf-app:auth-store');
    let userId: string | null = null;
    let userEmail: string | null = null;
    if (authRaw) {
      const parsed = JSON.parse(authRaw);
      const user = parsed?.state?.user;
      userId = user?.id ?? null;
      userEmail = user?.email ?? null;
    }

    const wsRaw = localStorage.getItem('hf-app:workspace');
    let workspaceId: string | null = null;
    let companyId: string | null = null;
    if (wsRaw) {
      const parsed = JSON.parse(wsRaw);
      workspaceId = parsed?.state?.activeWorkspaceId ?? null;
      companyId = parsed?.state?.activeCompanyId ?? null;
    }

    return { userId, userEmail, workspaceId, companyId };
  } catch {
    return { userId: null, userEmail: null, workspaceId: null, companyId: null };
  }
}

function buildReport(
  message: string,
  stack: string | undefined,
  type: BrowserErrorReport['type'],
): BrowserErrorReport {
  const userCtx = getUserContext();
  return {
    message: clamp(message, MAX_MESSAGE),
    stack: stack ? clamp(stack, MAX_STACK) : stack,
    type,
    pageUrl: window.location.href,
    route: window.location.pathname,
    ...userCtx,
    userAgent: navigator.userAgent,
    screenSize: `${window.screen.width}x${window.screen.height}`,
    breadcrumbs: [...breadcrumbs],
    occurrenceCount: 1,
    fingerprint: getFingerprint(message, stack),
  };
}

function sendReport(report: BrowserErrorReport): void {
  const url = `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}/client-errors/report`;

  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(report),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Ignore
  }
}

function reportError(
  message: string,
  stack: string | undefined,
  type: BrowserErrorReport['type'],
): void {
  if (isForeignError(message, stack)) return;

  const report = buildReport(message, stack, type);
  const fp = report.fingerprint;

  const existing = dedupMap.get(fp);
  if (existing) {
    existing.count++;
    existing.report = report;
    return;
  }

  const entry = {
    count: 1,
    report,
    // The first occurrence goes out immediately (below), so this flush carries
    // only what accumulated AFTER it — `count - 1`. Sending `count` here
    // re-sent that first occurrence a second time, which doubled every browser
    // row in the journal and made the counts operators triage from wrong by 2x.
    timer: setTimeout(() => {
      const e = dedupMap.get(fp);
      dedupMap.delete(fp);
      if (!e) return;
      const unsent = e.count - 1;
      if (unsent <= 0) return;
      e.report.occurrenceCount = unsent;
      sendReport(e.report);
    }, DEDUP_TTL_MS),
  };
  dedupMap.set(fp, entry);

  sendReport(report);
}

/**
 * Report a React error-boundary crash to the journal (ERR F6).
 *
 * A boundary CATCH (e.g. `app/phishing/error.tsx`) fires neither `window`
 * `error` nor `unhandledrejection`, so the listeners in
 * `initBrowserErrorReporter` never see it and the crash was invisible to
 * `error_issues`. Boundaries call this directly so the crash flows through the
 * same foreign-error filter, dedup and `/client-errors/report` POST as any
 * other browser error. Safe on the server (no-op without `window`).
 */
export function reportBoundaryError(error: unknown): void {
  if (typeof window === 'undefined') return;
  const message =
    error instanceof Error ? error.message : String(error ?? 'Unknown error');
  const stack = error instanceof Error ? error.stack : undefined;
  reportError(message, stack, 'Error');
}

export function initBrowserErrorReporter(): () => void {
  if (typeof window === 'undefined') return () => {};

  const origLog = console.log;
  const origWarn = console.warn;
  const origError = console.error;

  console.log = (...args: unknown[]) => {
    addBreadcrumb('Log', args);
    origLog.apply(console, args);
  };
  console.warn = (...args: unknown[]) => {
    addBreadcrumb('Warn', args);
    origWarn.apply(console, args);
  };
  console.error = (...args: unknown[]) => {
    addBreadcrumb('Error', args);
    origError.apply(console, args);
  };

  const onError = (event: ErrorEvent) => {
    reportError(
      event.message || 'Unknown error',
      event.error?.stack,
      'Error',
    );
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    const stack = reason instanceof Error ? reason.stack : undefined;
    reportError(message, stack, 'Unhandled');
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  const onBeforeUnload = () => {
    for (const [fp, entry] of dedupMap) {
      clearTimeout(entry.timer);
      // Same accounting as the dedup flush: occurrence #1 already went out.
      const unsent = entry.count - 1;
      if (unsent > 0) {
        entry.report.occurrenceCount = unsent;
        sendReport(entry.report);
      }
      dedupMap.delete(fp);
    }
  };
  window.addEventListener('beforeunload', onBeforeUnload);

  return () => {
    console.log = origLog;
    console.warn = origWarn;
    console.error = origError;
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    window.removeEventListener('beforeunload', onBeforeUnload);
  };
}
